'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.unixDate = exports.largeFloat = exports.bigPositiveInteger = exports.ethereumAddress = undefined;

var _web3Utils = require('web3-utils');

var _web3Utils2 = _interopRequireDefault(_web3Utils);

var _moment = require('moment');

var _moment2 = _interopRequireDefault(_moment);

var _chronoNode = require('chrono-node');

var _chronoNode2 = _interopRequireDefault(_chronoNode);

var _bignumber = require('bignumber.js');

var _isNumber = require('./isNumber');

var _isNumber2 = _interopRequireDefault(_isNumber);

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

// Factory functions to generate extension objects.

var ethereumAddress = exports.ethereumAddress = function ethereumAddress(joi) {
  return {
    base: joi.string().alphanum().length(42),
    name: 'ethereumAddress',
    language: {
      valid: 'must be in valid Ethereum address format - 42 characters, numbers and letters only.',
      checksum: 'must be in valid Ethereum address format - failed checksum check.'
    },
    pre: function pre(value, state, options) {
      // Force lowercase, check for valid format first.
      if (!_web3Utils2.default.isAddress(value.toLowerCase())) {
        return this.createError('ethereumAddress.valid', {}, state, options);
      }
      // If valid format, check again for checksum.
      if (!_web3Utils2.default.isAddress(value)) {
        return this.createError('ethereumAddress.checksum', {}, state, options);
      }
      return value;
    }
  };
};

var tokenSymbol = function tokenSymbol(joi) {
  return {
    base: joi.string().regex(/^[a-zA-Z]{3,5}$/).options({
      language: {
        string: {
          regex: {
            base: 'must be a valid token symbol (3-5 letters, all uppercase)',
            name: 'must be a valid token symbol (3-5 letters, all uppercase)'
          }
        }
      }
    }),
    name: 'tokenSymbol'
  };
};

var bigPositiveInteger = exports.bigPositiveInteger = function bigPositiveInteger(joi) {
  return {
    base: joi.string().regex(/^[1-9]\d{0,36}$/).options({
      language: {
        string: {
          regex: {
            base: 'must be a valid positive integer',
            name: 'must be a valid positive integer'
          }
        }
      }
    }),
    name: 'bigPositiveInteger'
  };
};

var largeFloat = exports.largeFloat = function largeFloat(joi) {
  return {
    base: joi.string(),
    name: 'largeFloat',
    language: {
      validNumber: 'must be a number',
      precision: 'must have no more than {{limit}} decimal places',
      min: 'must not be less than {{limit}}',
      max: 'must not be greater than {{limit}}',
      less: 'must be less than {{limit}}',
      greater: 'must be greater than {{limit}}'
    },
    pre: function pre(value, state, options) {
      // Check that it's a valid number.
      if (!(0, _isNumber2.default)(value)) {
        return this.createError('largeFloat.validNumber', { v: value }, state, options);
      }

      return value;
    },

    rules: [{
      name: 'precision',
      params: {
        limit: joi.number().required()
      },
      description: 'Large Float Precision',
      validate: function validate(params, value, state, options) {
        var limit = params.limit;

        // These are actually joi's own precision checks,
        // but doesn't lose anything from a convert to/from a number.

        var places = value.toString().match(/(?:\.(\d+))/);
        var decimalPlaces = places ? Math.max(places[1] ? places[1].length : 0, 0) : 0;

        if (decimalPlaces > parseInt(limit, 10)) {
          return this.createError('largeFloat.precision', { limit: limit }, state, options);
        }

        return value;
      }
    }, {
      name: 'min',
      params: {
        limit: joi.string().required()
      },
      description: 'Large Float min',
      validate: function validate(params, value, state, options) {
        var limit = params.limit;


        if ((0, _bignumber.BigNumber)(value).lt(limit)) {
          return this.createError('largeFloat.min', { limit: limit }, state, options);
        }

        return value;
      }
    }, {
      name: 'max',
      params: {
        limit: joi.string().required()
      },
      description: 'Large Float max',
      validate: function validate(params, value, state, options) {
        var limit = params.limit;


        if ((0, _bignumber.BigNumber)(value).gt(limit)) {
          return this.createError('largeFloat.max', { limit: limit }, state, options);
        }

        return value;
      }
    }, {
      name: 'less',
      params: {
        limit: joi.string().required()
      },
      description: 'Large Float less',
      validate: function validate(params, value, state, options) {
        var limit = params.limit;


        if ((0, _bignumber.BigNumber)(value).gte(limit)) {
          return this.createError('largeFloat.less', { limit: limit }, state, options);
        }

        return value;
      }
    }, {
      name: 'greater',
      params: {
        limit: joi.string().required()
      },
      description: 'Large Float greater',
      validate: function validate(params, value, state, options) {
        var limit = params.limit;


        if ((0, _bignumber.BigNumber)(value).lte(limit)) {
          return this.createError('largeFloat.greater', { limit: limit }, state, options);
        }

        return value;
      }
    }]
  };
};

/**
 * Transform a date string to a moment.
 * dateString can be formatted or human readable
 * See https://github.com/wanasit/chrono for supported formats.
 *
 * @param {string} dateString
 *  The date string to parse.
 */
var transformToDate = function transformToDate(dateString) {
  var date = _chronoNode2.default.parseDate(dateString);
  return (0, _moment2.default)(date).startOf('day');
};

var unixDate = exports.unixDate = function unixDate(joi) {
  return {
    base: joi.date().timestamp('unix'),
    name: 'unixDate',
    language: {
      min: 'must not be earlier than {{date}}',
      max: 'must not be later than {{date}}'
    },
    rules: [{
      name: 'min',
      params: {
        limit: joi.alternatives().try(joi.number().required(), joi.string().required())
      },
      description: 'Minimum Value',
      validate: function validate(params, value, state, options) {
        var limit = params.limit;

        var dateValue = {};

        // Format a date string to unix timestamp.
        if (typeof limit === 'string') {
          dateValue = transformToDate(limit);
        } else {
          dateValue = (0, _moment2.default)().unix(limit);
        }

        if ((0, _moment2.default)(value).diff(dateValue) < 0) {
          return this.createError('unixDate.min', {
            date: dateValue.format('MM/DD/YYYY')
          }, state, options);
        }

        return value;
      }
    }, {
      name: 'max',
      params: {
        limit: joi.alternatives().try(joi.number().required(), joi.string().required())
      },
      description: 'Maximum Value',
      validate: function validate(params, value, state, options) {
        var limit = params.limit;

        var dateValue = {};

        // Format a date string to unix timestamp.
        if (typeof limit === 'string') {
          dateValue = transformToDate(limit);
        } else {
          dateValue = (0, _moment2.default)().unix(limit);
        }

        if ((0, _moment2.default)(value).diff(dateValue) > 0) {
          return this.createError('unixDate.max', {
            date: dateValue.format('MM/DD/YYYY')
          }, state, options);
        }

        return value;
      }
    }]
  };
};

exports.default = [ethereumAddress, tokenSymbol, bigPositiveInteger, largeFloat, unixDate];


//////////////////
// WEBPACK FOOTER
// ./node_modules/@blockcatio/tabby-library/lib/utilities/joiExtensions.js
// module id = 468
// module chunks = 0